import React from 'react'
import { Button, Grid, Typography, Chip, TextField, Paper, MenuList, MenuItem, ClickAwayListener, CircularProgress, Box } from '@material-ui/core'
import MUIDataTable from 'mui-datatables'
import { useHistory } from 'react-router-dom'
import BackupIcon from '@material-ui/icons/Backup'
import PageTitle from '../../PageTitle/PageTitle'
import { FormLoader } from '../../../Loader/FormLoader'
import { FormattedMessage, useIntl } from 'react-intl'
import Configuration from '../../../../Services/Api/Configuration'
import { filterData } from '../../../../utils/helpers'
import { PromocodesFiltersPayload } from '../../../../Services/Payloads/AuthenticatedUserPayload'
import { useStyles } from './Styles'
import PromocodeMenu from './PromocodeMenu'
import { NamedRoutes } from '../../../../routes'
import { DiscountTypes } from '../../../../utils/enum'
import Moment from 'react-moment'
import moment from 'moment'
import 'moment/locale/ar-ly'
import PromocodeBulkImportForm from './PromocodeBulkImportForm'
import { PromocodeConsumer } from '../../../../PromocodeContext'
import Dialog from '../../Dialog'

function PromocodesContent({ handleNotificationCall, locale, performSearch, getPromocodes }) {
  let classes = useStyles()
  const intl = useIntl()
  const history = useHistory()
  const [count, setCount] = React.useState(0)
  const [currentFilter, setCurrentFilter] = React.useState(null)
  const rowsPerPage = Configuration.User_Config.promocodesLimit || 10
  const [sortOrder, setSortOrder] = React.useState({})
  const [data, setData] = React.useState([])
  const [loadedData, setLoadedData] = React.useState([])
  const [isLoading, setIsLoading] = React.useState(false)
  const [canCreate, setCanCreate] = React.useState(false)
  const [reloadPage, setReloadPage] = React.useState(false)

  // Bulk import state
  const [isBulkImportDialogOpen, setIsBulkImportDialogOpen] = React.useState(false)

  // Bulk import handlers
  const handleBulkImportDialogOpen = () => {
    setIsBulkImportDialogOpen(true)
  }

  const handleBulkImportDialogClose = (clearResponse) => {
    setIsBulkImportDialogOpen(false)
    if (clearResponse) {
      clearResponse()
    }
  }

  // Generic search state management
  const [searchStates, setSearchStates] = React.useState({
    users: { text: '', isSearching: false, results: [], showDropdown: false, selected: {} },
    userGroups: { text: '', isSearching: false, results: [], showDropdown: false, selected: {} },
    externalEntityGroups: { text: '', isSearching: false, results: [], showDropdown: false, selected: {} }
  })

  const debounceTimers = React.useRef({
    users: null,
    userGroups: null,
    externalEntityGroups: null
  })

  const columns = [
    {
      name: 'title',
      label: intl.formatMessage({ id: 'Promocodes.List.Table.Name' }),
      options: {
        filterType: 'textField',
        setCellHeaderProps: () => ({
          style: { minWidth: "250px" },
        }),
        customFilterListOptions: {
          render: (v) => {
            return (
              <div className={classes.FilterChip}>
                <FormattedMessage id='Promocodes.List.Table.Name' />:
                <span>{v}</span>
              </div>
            )
          }
        }
      }
    },
    {
      name: 'code',
      label: intl.formatMessage({ id: 'Promocodes.List.Table.Code' }),
      options: {
        filterType: 'textField',
        customFilterListOptions: {
          render: (v) => {
            return (
              <div className={classes.FilterChip}>
                <FormattedMessage id='Promocodes.List.Table.Code' />:
                <span>{v}</span>
              </div>
            )
          }
        }
      }
    },
    {
      name: 'discount_type',
      label: intl.formatMessage({ id: 'Promocodes.List.Table.DiscountType' }),
      options: {
        filterType: 'dropdown',
        filterOptions: {
          names: DiscountTypes.map(type => type.value),
          renderValue: (value) => {
            const type = DiscountTypes.find(t => t.value === value)
            return type ? intl.formatMessage({ id: type.label }) : value
          }
        },
        customFilterListOptions: {
          render: (v) => {
            const type = DiscountTypes.find(t => t.value === v)
            const displayValue = type ? intl.formatMessage({ id: type.label }) : v
            return (
              <div className={classes.FilterChip}>
                <FormattedMessage id='Promocodes.List.Table.DiscountType' />:
                <span>{displayValue}</span>
              </div>
            )
          }
        },
        customBodyRender: (value) => {
          const type = DiscountTypes.find(t => t.value === value)
          return type ? intl.formatMessage({ id: type.label }) : value
        }
      }
    },
    {
      name: 'discount_value',
      label: intl.formatMessage({ id: 'Promocodes.List.Table.DiscountValue' }),
      options: {
        filter: false,
        customBodyRender: (value, tableMeta) => {
          const discountType = tableMeta.rowData[2] // discount_type column
          return discountType === 'percentage' ? `${value}%` : value
        }
      }
    },
    {
      name: 'max_discount',
      label: intl.formatMessage({ id: 'Promocodes.List.Table.MaxDiscount' }),
      options: {
        filter: false
      }
    },
    {
      name: 'start_date',
      label: intl.formatMessage({ id: 'Promocodes.List.Table.StartDate' }),
      options: {
        filter: true,
        filterType: 'custom',
        setCellHeaderProps: () => ({
          style: { minWidth: "150px" },
        }),
        customFilterListOptions: {
          render: (v) => {
            return (
              <div className={classes.FilterChip}>
                <FormattedMessage id='Promocodes.List.Table.StartDate' />:
                <span><Moment locale={locale === 'ar' ? 'ar-ly' : 'en-us'} format="Do MMMM YYYY">{v[0]}</Moment></span>
              </div>
            )
          }
        },
        filterOptions: {
          display: (filterList, onChange, index, column) => {
            return (
              <div className={classes.filterWrapper}>
                <TextField
                  label={intl.formatMessage({ id: 'Promocodes.List.Table.StartDate' })}
                  type="date"
                  fullWidth
                  value={filterList[index][0] || ''}
                  onChange={(e) => {
                    filterList[index][0] = e.target.value
                    onChange(filterList[index], index, column)
                  }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </div>
            )
          }
        },
        customBodyRenderLite: (dataIndex) => {
          return data[dataIndex] &&
            <Moment locale={locale === 'ar' ? 'ar-ly' : 'en-us'}
              format="Do MMMM YYYY">{data[dataIndex].start_date}</Moment>
        }
      }
    },
    {
      name: 'end_date',
      label: intl.formatMessage({ id: 'Promocodes.List.Table.EndDate' }),
      options: {
        filter: true,
        filterType: 'custom',
        setCellHeaderProps: () => ({
          style: { minWidth: "150px" },
        }),
        customFilterListOptions: {
          render: (v) => {
            return (
              <div className={classes.FilterChip}>
                <FormattedMessage id='Promocodes.List.Table.EndDate' />:
                <span><Moment locale={locale === 'ar' ? 'ar-ly' : 'en-us'} format="Do MMMM YYYY">{v[0]}</Moment></span>
              </div>
            )
          }
        },
        filterOptions: {
          display: (filterList, onChange, index, column) => {
            return (
              <div className={classes.filterWrapper}>
                <TextField
                  label={intl.formatMessage({ id: 'Promocodes.List.Table.EndDate' })}
                  type="date"
                  fullWidth
                  value={filterList[index][0] || ''}
                  onChange={(e) => {
                    filterList[index][0] = e.target.value
                    onChange(filterList[index], index, column)
                  }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </div>
            )
          }
        },
        customBodyRenderLite: (dataIndex) => {
          return data[dataIndex] &&
            <Moment locale={locale === 'ar' ? 'ar-ly' : 'en-us'}
              format="Do MMMM YYYY">{data[dataIndex].end_date}</Moment>
        }
      }
    },
    {
      name: 'is_active',
      label: intl.formatMessage({ id: 'Promocodes.List.Table.IsActive' }),
      options: {
        filterType: 'dropdown',
        filterOptions: {
          names: ['1', '0'],
          renderValue: (value) => {
            if (value === '1') {
              return intl.formatMessage({ id: 'Common.Active' })
            }
            return intl.formatMessage({ id: 'Common.Inactive' })
          }
        },
        customFilterListOptions: {
          render: (v) => {
            if (v === '1' || v === '0') {
              const displayValue = v === '1'
                ? intl.formatMessage({ id: 'Common.Active' })
                : intl.formatMessage({ id: 'Common.Inactive' })
              return (
                <div className={classes.FilterChip}>
                  <FormattedMessage id='Promocodes.List.Table.IsActive' />:
                  <span>{displayValue}</span>
                </div>
              )
            }
            return null
          }
        },
        customBodyRender: (value) => {
          return (
            <Chip
              label={value ? intl.formatMessage({ id: 'Common.Active' }) : intl.formatMessage({ id: 'Common.Inactive' })}
              className={value ? classes.activeChip : classes.inactiveChip}
              size="small"
            />
          )
        }
      }
    },
    {
      name: 'usage_limit',
      label: intl.formatMessage({ id: 'Promocodes.List.Table.UsageLimit' }),
      options: {
        filter: false
      }
    },
    {
      name: 'usage_count',
      label: intl.formatMessage({ id: 'Promocodes.List.Table.UsageCount' }),
      options: {
        filter: false
      }
    },
    {
      name: 'is_currently_active',
      label: intl.formatMessage({ id: 'Promocodes.List.Table.CurrentlyActive' }),
      options: {
        filter: false,
        customBodyRender: (value) => {
          return (
            <Chip
              label={value ? intl.formatMessage({ id: 'Common.Yes' }) : intl.formatMessage({ id: 'Common.No' })}
              className={value ? classes.activeChip : classes.inactiveChip}
              size="small"
            />
          )
        }
      }
    },
    {
      name: 'has_reached_usage_limit',
      label: intl.formatMessage({ id: 'Promocodes.List.Table.ReachedLimit' }),
      options: {
        filter: false,
        customBodyRender: (value) => {
          return (
            <Chip
              label={value ? intl.formatMessage({ id: 'Common.Yes' }) : intl.formatMessage({ id: 'Common.No' })}
              className={value ? classes.inactiveChip : classes.activeChip}
              size="small"
            />
          )
        }
      }
    },
    {
      name: 'created_at',
      label: intl.formatMessage({ id: 'Promocodes.List.Table.CreatedAt' }),
      options: {
        filter: false,
        setCellHeaderProps: () => ({
          style: { minWidth: "150px" },
        }),
        customBodyRenderLite: (dataIndex) => {
          return data[dataIndex] &&
            <Moment locale={locale === 'ar' ? 'ar-ly' : 'en-us'}
              format="Do MMMM YYYY h:mm a">{data[dataIndex].created_at}</Moment>
        }
      }
    },
    {
      name: 'user_id',
      options: {
        display: false,
        print: false,
        download: false,
        filter: true,
        sort: false,
        viewColumns: false,
        filterType: 'custom',
        customFilterListOptions: {
          render: (v) => {
            if (v && v.length > 0 && v[0]) {
              const user = v[0]
              return (
                <div className={classes.FilterChip}>
                  <FormattedMessage id='Promocodes.List.Table.User' />:
                  <span>{user?.first_name} {user?.last_name}</span>
                </div>
              )
            }
            return null
          },
          update: (filterList, filterPos, index) => {
            handleItemRemove('users', 'user_id')
            filterList[index] = []
            return filterList
          }
        },
        filterOptions: {
          display: (filterList, onChange, index, column) => {
            const selectedUserInFilter = filterList[index] && filterList[index][0] ? filterList[index][0] : null
            const displayValue = selectedUserInFilter
              ? `${selectedUserInFilter.first_name} ${selectedUserInFilter.last_name}`
              : searchStates.users.text
            return (
              <div className={classes.filterWrapper}>
                <ClickAwayListener onClickAway={() => setSearchStates(prev => ({ ...prev, users: { ...prev.users, showDropdown: false } }))}>
                  <div className={classes.autocompleteWrapper}>
                    <TextField
                      label={intl.formatMessage({ id: 'Promocodes.List.Filter.SearchUsers' })}
                      placeholder={intl.formatMessage({ id: 'Promocodes.List.Filter.SearchUsersPlaceholder' })}
                      fullWidth
                      value={displayValue}
                      onChange={(e) => {
                        if (selectedUserInFilter) {
                          filterList[index] = []
                          onChange(filterList[index], index, column)
                          handleItemRemove('users', 'user_id')
                        }
                        handleSearchChange('users', e.target.value)
                      }}
                      onFocus={() => {
                        if (searchStates.users.results.length > 0) {
                          setSearchStates(prev => ({ ...prev, users: { ...prev.users, showDropdown: true } }))
                        }
                      }}
                      helperText={searchStates.users.text.trim().length === 1 ? intl.formatMessage({ id: 'Promocodes.List.Error.User.Length' }) : !!searchStates.users.text.trim().length && !Object.keys(searchStates.users.selected).length && intl.formatMessage({ id: 'Promocodes.List.Error.User.Empty' })}
                      error={!!searchStates.users.text.trim().length && !Object.keys(searchStates.users.selected).length}
                      InputProps={{
                        endAdornment: searchStates.users.isSearching && <CircularProgress size={20} />
                      }}
                    />
                    {searchStates.users.showDropdown && (
                      <Paper className={classes.dropdown}>
                        {
                          searchStates.users.results.length > 0 ?
                            <MenuList>
                              {searchStates.users.results.map((user) => (
                                <MenuItem
                                  key={user.id}
                                  onClick={() => {
                                    handleItemSelect('users', user)
                                    filterList[index][0] = user
                                    onChange(filterList[index], index, column)
                                  }}
                                  className={classes.menuItem}
                                >
                                  <div>
                                    <div className={classes.itemName}>
                                      {user.first_name} {user.last_name}
                                    </div>
                                    <div className={classes.itemDetail}>
                                      {user.email}
                                    </div>
                                  </div>
                                </MenuItem>
                              ))}
                            </MenuList>
                            :
                            <Typography variant="body1" className={classes.noResultsText}>
                              {intl.formatMessage({ id: 'Promocodes.List.Users.NoResults' })}
                            </Typography>
                        }
                      </Paper>
                    )}
                  </div>
                </ClickAwayListener>
              </div>
            )
          }
        }
      }
    },
    {
      name: 'external_entity_reference',
      label: intl.formatMessage({ id: 'Promocodes.List.Filter.ExternalEntity' }),
      options: {
        display: false,
        print: false,
        download: false,
        filter: true,
        sort: false,
        viewColumns: false,
        filterType: 'textField',
        customFilterListOptions: {
          render: (v) => {
            return (
              <div className={classes.FilterChip}>
                <FormattedMessage id='Promocodes.List.Filter.ExternalEntity' />:
                <span>{v}</span>
              </div>
            )
          }
        }
      }
    },
    {
      name: 'user_group_id',
      label: intl.formatMessage({ id: 'Promocodes.List.Table.UserGroup' }),
      options: {
        display: false,
        print: false,
        download: false,
        filter: true,
        sort: false,
        viewColumns: false,
        filterType: 'custom',
        customFilterListOptions: {
          render: (v) => {
            if (v && v.length > 0 && v[0]) {
              const userGroup = v[0]
              return (
                <div className={classes.FilterChip}>
                  <FormattedMessage id='Promocodes.List.Table.UserGroup' />:
                  <span>{userGroup?.name}</span>
                </div>
              )
            }
            return null
          },
          update: (filterList, filterPos, index) => {
            handleItemRemove('userGroups', 'user_group_id')
            filterList[index] = []
            return filterList
          }
        },
        filterOptions: {
          display: (filterList, onChange, index, column) => {
            const selectedUserGroupInFilter = filterList[index] && filterList[index][0] ? filterList[index][0] : null
            const displayValue = selectedUserGroupInFilter
              ? selectedUserGroupInFilter.name
              : searchStates.userGroups.text
            return (
              <div className={classes.filterWrapper}>
                <ClickAwayListener onClickAway={() => setSearchStates(prev => ({ ...prev, userGroups: { ...prev.userGroups, showDropdown: false } }))}>
                  <div className={classes.autocompleteWrapper}>
                    <TextField
                      label={intl.formatMessage({ id: 'Promocodes.List.Filter.SearchUserGroups' })}
                      placeholder={intl.formatMessage({ id: 'Promocodes.List.Filter.SearchUserGroupsPlaceholder' })}
                      fullWidth
                      value={displayValue}
                      onChange={(e) => {
                        if (selectedUserGroupInFilter) {
                          filterList[index] = []
                          onChange(filterList[index], index, column)
                          handleItemRemove('userGroups', 'user_group_id')
                        }
                        handleSearchChange('userGroups', e.target.value)
                      }}
                      onFocus={() => {
                        if (searchStates.userGroups.results.length > 0) {
                          setSearchStates(prev => ({ ...prev, userGroups: { ...prev.userGroups, showDropdown: true } }))
                        }
                      }}
                      helperText={searchStates.userGroups.text.trim().length === 1 ? intl.formatMessage({ id: 'Promocodes.List.Error.UserGroup.Length' }) : !!searchStates.userGroups.text.trim().length && !Object.keys(searchStates.userGroups.selected).length && intl.formatMessage({ id: 'Promocodes.List.Error.UserGroup.Empty' })}
                      error={!!searchStates.userGroups.text.trim().length && !Object.keys(searchStates.userGroups.selected).length}
                      InputProps={{
                        endAdornment: searchStates.userGroups.isSearching && <CircularProgress size={20} />
                      }}
                    />
                    {searchStates.userGroups.showDropdown && (
                      <Paper className={classes.dropdown}>
                        {
                          searchStates.userGroups.results.length > 0 ?
                            <MenuList>
                              {searchStates.userGroups.results.map((userGroup) => (
                                <MenuItem
                                  key={userGroup.id}
                                  onClick={() => {
                                    handleItemSelect('userGroups', userGroup)
                                    filterList[index][0] = userGroup
                                    onChange(filterList[index], index, column)
                                  }}
                                  className={classes.menuItem}
                                >
                                  <div>
                                    <div className={classes.itemName}>
                                      {userGroup.name}
                                    </div>
                                  </div>
                                </MenuItem>
                              ))}
                            </MenuList>
                            :
                            <Typography variant="body1" className={classes.noResultsText}>
                              {intl.formatMessage({ id: 'Promocodes.List.UserGroups.NoResults' })}
                            </Typography>
                        }
                      </Paper>
                    )}
                  </div>
                </ClickAwayListener>
              </div>
            )
          }
        }
      }
    },
    {
      name: 'external_entity_group_id',
      label: intl.formatMessage({ id: 'Promocodes.List.Table.ExternalEntityGroup' }),
      options: {
        display: false,
        print: false,
        download: false,
        filter: true,
        sort: false,
        viewColumns: false,
        filterType: 'custom',
        customFilterListOptions: {
          render: (v) => {
            if (v && v.length > 0 && v[0]) {
              const externalEntityGroup = v[0]
              return (
                <div className={classes.FilterChip}>
                  <FormattedMessage id='Promocodes.List.Table.ExternalEntityGroup' />:
                  <span>{externalEntityGroup?.name}</span>
                </div>
              )
            }
            return null
          },
          update: (filterList, filterPos, index) => {
            handleItemRemove('externalEntityGroups', 'external_entity_group_id')
            filterList[index] = []
            return filterList
          }
        },
        filterOptions: {
          display: (filterList, onChange, index, column) => {
            const selectedExternalEntityGroupInFilter = filterList[index] && filterList[index][0] ? filterList[index][0] : null
            const displayValue = selectedExternalEntityGroupInFilter
              ? selectedExternalEntityGroupInFilter.name
              : searchStates.externalEntityGroups.text
            return (
              <div className={classes.filterWrapper}>
                <ClickAwayListener onClickAway={() => setSearchStates(prev => ({ ...prev, externalEntityGroups: { ...prev.externalEntityGroups, showDropdown: false } }))}>
                  <div className={classes.autocompleteWrapper}>
                    <TextField
                      label={intl.formatMessage({ id: 'Promocodes.List.Filter.SearchExternalEntityGroups' })}
                      placeholder={intl.formatMessage({ id: 'Promocodes.List.Filter.SearchExternalEntityGroupsPlaceholder' })}
                      fullWidth
                      value={displayValue}
                      onChange={(e) => {
                        if (selectedExternalEntityGroupInFilter) {
                          filterList[index] = []
                          onChange(filterList[index], index, column)
                          handleItemRemove('externalEntityGroups', 'external_entity_group_id')
                        }
                        handleSearchChange('externalEntityGroups', e.target.value)
                      }}
                      onFocus={() => {
                        if (searchStates.externalEntityGroups.results.length > 0) {
                          setSearchStates(prev => ({ ...prev, externalEntityGroups: { ...prev.externalEntityGroups, showDropdown: true } }))
                        }
                      }}
                      helperText={searchStates.externalEntityGroups.text.trim().length === 1 ? intl.formatMessage({ id: 'Promocodes.List.Error.ExternalEntityGroup.Length' }) : !!searchStates.externalEntityGroups.text.trim().length && !Object.keys(searchStates.externalEntityGroups.selected).length && intl.formatMessage({ id: 'Promocodes.List.Error.ExternalEntityGroup.Empty' })}
                      error={!!searchStates.externalEntityGroups.text.trim().length && !Object.keys(searchStates.externalEntityGroups.selected).length}
                      InputProps={{
                        endAdornment: searchStates.externalEntityGroups.isSearching && <CircularProgress size={20} />
                      }}
                    />
                    {searchStates.externalEntityGroups.showDropdown && (
                      <Paper className={classes.dropdown}>
                        {
                          searchStates.externalEntityGroups.results.length > 0 ?
                            <MenuList>
                              {searchStates.externalEntityGroups.results.map((externalEntityGroup) => (
                                <MenuItem
                                  key={externalEntityGroup.id}
                                  onClick={() => {
                                    handleItemSelect('externalEntityGroups', externalEntityGroup)
                                    filterList[index][0] = externalEntityGroup
                                    onChange(filterList[index], index, column)
                                  }}
                                  className={classes.menuItem}
                                >
                                  <div>
                                    <div className={classes.itemName}>
                                      {externalEntityGroup.name}
                                    </div>
                                  </div>
                                </MenuItem>
                              ))}
                            </MenuList>
                            :
                            <Typography variant="body1" className={classes.noResultsText}>
                              {intl.formatMessage({ id: 'Promocodes.List.ExternalEntityGroups.NoResults' })}
                            </Typography>
                        }
                      </Paper>
                    )}
                  </div>
                </ClickAwayListener>
              </div>
            )
          }
        }
      }
    },
    {
      name: '',
      options: {
        filter: false,
        print: false,
        viewColumns: false,
        download: false,
        sort: false,
        customBodyRenderLite: (dataIndex) => {
          return (data[dataIndex] &&
            <PromocodeMenu
              index={dataIndex}
              data={data}
              setData={setData}
              count={count}
              setCount={setCount}
              handleNotificationCall={handleNotificationCall} />)
        }
      }
    }
  ]

  // Cleanup debounce timers on unmount
  React.useEffect(() => {
    const timers = debounceTimers.current

    return () => {
      Object.values(timers).forEach(timer => {
        if (timer) clearTimeout(timer)
      })
    }
  }, [])

  // Generic search handler
  const handleSearch = async (searchType, searchTerm) => {
    if (searchTerm.length < 2) {
      setSearchStates(prev => ({
        ...prev,
        [searchType]: { ...prev[searchType], results: [], showDropdown: false }
      }))
      return
    }

    setSearchStates(prev => ({
      ...prev,
      [searchType]: { ...prev[searchType], isSearching: true }
    }))

    const result = await performSearch(searchType, searchTerm)

    if (result.success) {
      setSearchStates(prev => ({
        ...prev,
        [searchType]: {
          ...prev[searchType],
          results: result.data,
          showDropdown: true,
          isSearching: false
        }
      }))
    } else {
      handleNotificationCall({
        type: 'defence',
        message: intl.formatMessage({ id: 'BasicInfo.FormErrorAlert' }),
        color: 'error'
      }, 'error')
      setSearchStates(prev => ({
        ...prev,
        [searchType]: {
          ...prev[searchType],
          results: [],
          showDropdown: false,
          isSearching: false
        }
      }))
    }
  }

  // Generic search change handler with debounce
  const handleSearchChange = (searchType, value) => {
    setSearchStates(prev => ({
      ...prev,
      [searchType]: { ...prev[searchType], text: value }
    }))

    if (debounceTimers.current[searchType]) {
      clearTimeout(debounceTimers.current[searchType])
    }

    debounceTimers.current[searchType] = setTimeout(() => {
      handleSearch(searchType, value)
    }, 500)
  }

  // Generic item select handler
  const handleItemSelect = (searchType, item) => {
    setSearchStates(prev => ({
      ...prev,
      [searchType]: {
        text: '',
        isSearching: false,
        results: [],
        showDropdown: false,
        selected: item
      }
    }))
  }

  // Generic item remove handler
  const handleItemRemove = (searchType, filterKey) => {
    setSearchStates(prev => ({
      ...prev,
      [searchType]: {
        text: '',
        isSearching: false,
        results: [],
        showDropdown: false,
        selected: {}
      }
    }))

    const { [filterKey]: _, ...restFilters } = currentFilter || {}
    setCurrentFilter(Object.keys(restFilters).length > 0 ? restFilters : {})
  }

  // get data
  const getPromocodesListData = async (page) => {
    xhrRequest(page)
  }

  // Fetch promocodes data
  const xhrRequest = async (page, sortOrder = {}, clear) => {
    setIsLoading(true)

    if (!!loadedData[page] && !!loadedData[page].length && !clear) {
      setData(loadedData[page])
      setIsLoading(false)
      return
    }

    const params = filterData(PromocodesFiltersPayload(page, rowsPerPage, currentFilter))
    const result = await getPromocodes(params)

    if (!result.success) {
      let message = intl.formatMessage({ id: 'BasicInfo.FormErrorAlert' })
      if (result.error?.response?.data?.status) {
        const status = result.error.response.status
        const statusData = result.error.response.data.status

        if (status === 403) {
          setData([])
          message = intl.formatMessage({ id: 'BasicInfo.AccessDeniedAlert' })
        } else if (statusData.errors && status === 422) {
          message = statusData.errors
        } else if (statusData.message) {
          message = statusData.message
        }
      }

      handleNotificationCall({
        type: 'defence',
        message: message,
        color: 'error'
      }, 'error')
      setIsLoading(false)
      return
    }

    setCanCreate(true)
    let newData = result.data
    setSortOrder(sortOrder)
    let sortField = sortOrder.name
    let sortDir = sortOrder.direction

    if (sortField) {
      newData = newData.sort((a, b) => {
        if (a[sortField] < b[sortField]) {
          return 1 * (sortDir === 'asc' ? -1 : 1)
        } else if (a[sortField] > b[sortField]) {
          return -1 * (sortDir === 'asc' ? -1 : 1)
        } else {
          return 0
        }
      })
    }

    setData(newData)
    let prevData = loadedData
    prevData[page] = newData
    setLoadedData(prevData)
    setCount(result.meta.total || 0)
    setIsLoading(false)
  }

  const sort = (page, sortOrder) => {
    setSortOrder(sortOrder)
    let sortField = sortOrder.name
    let sortDir = sortOrder.direction
    let newData = data

    if (sortField) {
      newData = newData.sort((a, b) => {
        if (a[sortField] < b[sortField]) {
          return 1 * (sortDir === 'asc' ? -1 : 1)
        } else if (a[sortField] > b[sortField]) {
          return -1 * (sortDir === 'asc' ? -1 : 1)
        } else {
          return 0
        }
      })
    }
    setData(newData)
  }

  React.useEffect(() => {
    getPromocodesListData(1)
  }, // eslint-disable-next-line react-hooks/exhaustive-deps
    [])

  const changePage = (page, sortOrder) => {
    xhrRequest(++page, sortOrder)
  }

  const changeFilter = (applyFilters) => {
    setLoadedData([])
    let filterList = applyFilters()
    let filters = {}
    filterList.forEach((filterItem, index) => {
      if (!!filterItem.length) {
        const columnName = columns[index].name

        // Handle special filters
        if (columnName === 'user_id' && filterItem[0] && typeof filterItem[0] === 'object') {
          filters.user_id = filterItem[0].id
        } else if (columnName === 'user_group_id' && filterItem[0] && typeof filterItem[0] === 'object') {
          filters.user_group_id = filterItem[0].id
        } else if (columnName === 'external_entity_group_id' && filterItem[0] && typeof filterItem[0] === 'object') {
          filters.external_entity_group_id = filterItem[0].id
        } else if (columnName === 'start_date' && filterItem[0] && filterItem[0].length >= 1) {
          filters.start_date_from = moment(filterItem[0]).format('YYYY-MM-DD')
        } else if (columnName === 'end_date' && filterItem[0] && filterItem[0].length >= 1) {
          filters.end_date_to = moment(filterItem[0]).format('YYYY-MM-DD')
        } else if (columnName === 'is_active' && filterItem[0] !== '' && filterItem[0] !== undefined) {
          filters.is_active = filterItem[0]
        } else if (filterItem[0] && filterItem[0].length >= 1) {
          filters[columnName] = filterItem[0]
        }
      }
    })

    setCurrentFilter(filters)
  }

  React.useEffect(() => {
    if (reloadPage) {
      setLoadedData([])
      xhrRequest(1, {}, true)
      setReloadPage(false)
    }
  }, // eslint-disable-next-line react-hooks/exhaustive-deps
    [reloadPage])

  React.useEffect(() => {
    if (!!currentFilter) {
      xhrRequest(1, {}, true)
    }
  }, // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentFilter])

  const options = {
    filter: true,
    filterType: 'dropdown',
    responsive: 'simple',
    selectableRowsHeader: false,
    selectableRowsHideCheckboxes: true,
    serverSide: true,
    count: count,
    search: false,
    rowsPerPage: rowsPerPage,
    rowsPerPageOptions: [],
    sortOrder: sortOrder,
    confirmFilters: true,
    downloadOptions: { filename: `${intl.formatMessage({ id: 'Sidebar.Links.Promocodes' })}.csv`, separator: ',' },
    customFilterDialogFooter: (currentFilterList, applyNewFilters) => {
      const hasInvalidUserSearch = !!searchStates.users.text.trim().length && !Object.keys(searchStates.users.selected).length
      const hasInvalidUserGroupSearch = !!searchStates.userGroups.text.trim().length && !Object.keys(searchStates.userGroups.selected).length
      const hasInvalidExternalEntityGroupSearch = !!searchStates.externalEntityGroups.text.trim().length && !Object.keys(searchStates.externalEntityGroups.selected).length

      return (
        <div className={classes.ApplyFilterWrapper}>
          <Button color='primary' variant="contained"
            disabled={hasInvalidUserSearch || hasInvalidUserGroupSearch || hasInvalidExternalEntityGroupSearch}
            onClick={() => changeFilter(applyNewFilters)}>
            <FormattedMessage id='Users.List.Table.ApplyFilters' /></Button>
        </div>
      )
    },
    onFilterChange: (column, filterList, type) => {
      if (type === 'chip') {
        setLoadedData([])
        let newFilters = () => (filterList)
        changeFilter(newFilters)
      }
    },
    textLabels: {
      body: {
        noMatch: intl.formatMessage({ id: 'Promocodes.List.Table.NoMatching' }),
        toolTip: intl.formatMessage({ id: 'Users.List.Table.Sort' }),
        columnHeaderTooltip: column => intl.formatMessage(
          { id: 'Users.List.Table.SortFor' },
          { label: column.label })
      },
      pagination: {
        next: intl.formatMessage({ id: 'Users.List.Table.NextPage' }),
        previous: intl.formatMessage({ id: 'Users.List.Table.PreviousPage' }),
        rowsPerPage: intl.formatMessage({ id: 'Users.List.Table.RowsPerPage' }),
        displayRows: intl.formatMessage({ id: 'Users.List.Table.Of' })
      },
      toolbar: {
        search: intl.formatMessage({ id: 'Users.List.Table.Search' }),
        downloadCsv: intl.formatMessage({ id: 'Users.List.Table.Download' }),
        print: intl.formatMessage({ id: 'Users.List.Table.Print' }),
        viewColumns: intl.formatMessage({ id: 'Users.List.Table.ViewColumns' }),
        filterTable: intl.formatMessage({ id: 'Users.List.Table.FilterTable' })
      },
      filter: {
        all: intl.formatMessage({ id: 'Users.List.Table.All' }),
        title: intl.formatMessage({ id: 'Users.List.Table.FILTERS' }),
        reset: intl.formatMessage({ id: 'Users.List.Table.RESET' })
      },
      viewColumns: {
        title: intl.formatMessage({ id: 'Users.List.Table.ShowColumns' }),
        titleAria: intl.formatMessage({ id: 'Users.List.Table.ShowHideColumns' })
      }
    },
    onTableChange: (action, tableState) => {
      switch (action) {
        case 'changePage':
          changePage(tableState.page, tableState.sortOrder)
          break
        case 'sort':
          sort(tableState.page, tableState.sortOrder)
          break
        default:
          break
      }
    }
  }

  return (
    <PromocodeConsumer>
      {({ handleBulkImportSubmit, bulkImportHasError, clearBulkImportError }) => (
        <>
          <PageTitle title={<FormattedMessage id='Sidebar.Links.Promocodes' />}
            button={!!canCreate && (
              <Box display="flex" className={classes.buttonsContainer}>
                <Button
                  variant="outlined"
                  size="large"
                  color="primary"
                  onClick={handleBulkImportDialogOpen}
                  startIcon={<BackupIcon />}
                >
                  <FormattedMessage id='PromocodeBulkImport.button.title' />
                </Button>
                <Button
                  classes={{ root: classes.button }}
                  variant="contained"
                  size="large"
                  color="primary"
                  onClick={() => {
                    history.push(NamedRoutes.subscription.promocodes.add)
                  }}
                >
                  <FormattedMessage id='Promocodes.List.AddPromocode.Title' />
                </Button>
              </Box>
            )} />
          <Grid container spacing={4}>
            <Grid item xs={12} className={classes.TableWrapper}>
              {isLoading && <FormLoader loading={true} />}
              <MUIDataTable
                title={
                  <Typography variant="h6">
                    <FormattedMessage id='Promocodes.List.Table.Title' />
                  </Typography>
                }
                data={data}
                columns={columns}
                options={options}
              />
            </Grid>
          </Grid>

          {/* Bulk Import Dialog */}
          <Dialog
            isOpen={isBulkImportDialogOpen}
            onClose={() => handleBulkImportDialogClose(clearBulkImportError)}
            maxWidth="sm"
            fullWidth
            title={<FormattedMessage id='PromocodeBulkImport.dialog.title' />}
          >
            <PromocodeBulkImportForm
              onSubmit={handleBulkImportSubmit}
              cancelDialog={() => handleBulkImportDialogClose(clearBulkImportError)}
              bulkImportHasError={bulkImportHasError}
              clearBulkImportError={clearBulkImportError}
              reloadPage={setReloadPage}
            />
          </Dialog>
        </>
      )}
    </PromocodeConsumer>
  )
}

export default PromocodesContent